import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, BrowserRouter, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import '../styles/theme.scss';
import { changeTheme } from './actions/layout';

const AdminApp = lazy(() => import('../admin/App'));
const LayoutComponent = lazy(() => import('./components/Layout/Layout'));
const Login = lazy(() => import('../auth/login/Login'));
const Reset = lazy(() => import('../auth/reset/Reset'));
const Forgot = lazy(() => import('../auth/forgot/Forgot'));
const ErrorPage = lazy(() => import('./pages/error/ErrorPage'));
const Disclaimer = lazy(() => import('./pages/disclaimer/Disclaimer'));

const CloseButton = ({ closeToast }) => <i onClick={closeToast} className="la la-close notifications-close" />

// Custom ToastContainer wrapper to avoid defaultProps warning
const CustomToastContainer = (props) => {
  // Force dark theme for toasts
  const currentTheme = 'dark';
  const defaultProps = {
    autoClose: 5000,
    hideProgressBar: true,
    position: "top-right",
    theme: currentTheme === 'dark' ? 'dark' : 'light',
    limit: 3,
    newestOnTop: true,
    pauseOnFocusLoss: false,
    draggable: true,
    draggablePercent: 60,
    ...props
  };

  return <ToastContainer {...defaultProps} />;
};

// Global flag to prevent redirect loops
let isRedirecting = false;

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const storedTheme = localStorage.getItem('dashboardTheme');
    if (storedTheme) {
      let effectiveTheme = storedTheme;
      if (storedTheme === 'auto') {
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      dispatch(changeTheme(effectiveTheme));
    }
  }, [dispatch]);

  // Wrapper to inject location into connected class component (React Router v6)
  const LayoutRouteWrapper = () => {
    const location = useLocation();
    const dispatch = useDispatch();

    // CRITICAL: Check if admin user trying to access user routes
    const adminMode = sessionStorage.getItem('admin_mode');
    if (adminMode === 'admin') {
      // Prevent re-entry during redirect
      if (isRedirecting) {
        return null;
      }

      // Force hard redirect to break any loops
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
        isRedirecting = true;

        // Use setTimeout to break out of React render cycle
        setTimeout(() => {
          window.location.replace('/admin');
        }, 0);
      }

      return null; // Don't render Layout
    }

    return <LayoutComponent dispatch={dispatch} location={location} />;
  };

  // Smart root redirect - checks user type before redirecting
  const SmartRootRedirect = () => {
    const adminMode = sessionStorage.getItem('admin_mode');

    // App uses cookie-based auth — no token in sessionStorage. Check adminMode only.
    if (adminMode === 'admin') {
      if (!isRedirecting && typeof window !== 'undefined') {
        isRedirecting = true;
        setTimeout(() => {
          window.location.replace('/admin');
        }, 0);
      }
      return null;
    }

    return <Navigate to="/dashboard" replace />;
  };

  return (
    <ErrorBoundary>
      <CustomToastContainer
        closeButton={<CloseButton />}
      />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Suspense fallback={null}>
          <Routes>
            {/* Smart root redirect - checks if admin or user */}
            <Route path="/" element={<SmartRootRedirect />} />

            {/* Admin Routes - Must come before catch-all */}
            <Route path="/admin/*" element={<AdminApp />} />

            {/* Auth Routes - Must come before catch-all */}
            <Route path="/login" element={<Login />} />
            <Route path="/password-reset" element={<Reset />} />
            <Route path="/forgot" element={<Forgot />} />

            {/* Public Routes */}
            <Route path="/disclaimer" element={<Disclaimer />} />

            {/* Error Routes */}
            <Route path="/error" element={<ErrorPage />} />

            {/* User Routes - Catch-all for user pages */}
            <Route path="/*" element={<LayoutRouteWrapper />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
